import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ApiError, errorText, type Meta } from '../api';
import { adminApi, type Identity } from './api';
import { AccountPage } from './account';
import { BrandEditPage } from './brand-edit';
import { BrandListPage } from './brands';
import { BookingsPage } from './bookings';
import { ConnectPage } from './connect';
import { DaysPage } from './days';
import { GooglePage } from './google';
import { IntegrationsPage } from './integrations';
import { ForgotPasswordPage, ResetPasswordPage } from './reset';

/** Die Verwaltung unter /admin. Seitenwechsel ohne Neuladen über history.pushState. */

export function navigate(path: string): void {
  window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function usePath(): string {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const onChange = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onChange);
    return () => window.removeEventListener('popstate', onChange);
  }, []);
  return path;
}

export function Link({ href, className, children }: { href: string; className?: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      className={className}
      onClick={(e) => {
        e.preventDefault();
        navigate(href);
      }}
    >
      {children}
    </a>
  );
}

export function AdminApp({ meta, resetToken }: { meta: Meta; resetToken?: string | undefined }) {
  const path = usePath();
  const [identity, setIdentity] = useState<Identity | null | undefined>(undefined);
  const [forgot, setForgot] = useState(false);

  const refresh = useCallback(() => {
    adminApi
      .me()
      .then(setIdentity)
      .catch(() => setIdentity(null));
  }, []);

  useEffect(refresh, [refresh]);

  useEffect(() => {
    document.title = `${meta.productName} – Verwaltung`;
  }, [meta.productName]);

  if (identity === undefined) return null;
  if (identity === null && resetToken) return <ResetPasswordPage meta={meta} token={resetToken} onDone={setIdentity} />;
  if (identity === null && forgot) return <ForgotPasswordPage meta={meta} onBack={() => setForgot(false)} />;
  if (identity === null) return <LoginPage meta={meta} onLogin={setIdentity} onForgot={() => setForgot(true)} />;

  async function logout() {
    await adminApi.logout().catch(() => undefined);
    setIdentity(null);
    navigate('/admin');
  }

  const brand = /^\/admin\/auftritte\/([0-9a-f-]{36})\/?$/.exec(path);
  let page: React.ReactNode;
  if (path === '/admin/konto') page = <AccountPage identity={identity} />;
  else if (path === '/admin/buchungen') page = <BookingsPage />;
  else if (path === '/admin/tage') page = <DaysPage />;
  else if (path === '/admin/ki') page = <ConnectPage />;
  else if (path === '/admin/anbindungen') page = <IntegrationsPage />;
  else if (path === '/admin/google') page = <GooglePage />;
  else if (brand) page = <BrandEditPage id={brand[1]!} />;
  else page = <BrandListPage />;

  return (
    <>
      <header className="admin-bar">
        <Link className="admin-bar__brand" href="/admin">
          {meta.productName}
        </Link>
        <nav className="admin-bar__nav">
          <Link href="/admin" className={path === '/admin' || path.startsWith('/admin/auftritte') ? 'is-active' : ''}>
            Auftritte
          </Link>
          <Link href="/admin/buchungen" className={path === '/admin/buchungen' ? 'is-active' : ''}>
            Buchungen
          </Link>
          <Link href="/admin/tage" className={path === '/admin/tage' ? 'is-active' : ''}>
            Tage
          </Link>
          <Link href="/admin/google" className={path === '/admin/google' ? 'is-active' : ''}>
            Google-Kalender
          </Link>
          <Link href="/admin/ki" className={path === '/admin/ki' ? 'is-active' : ''}>
            KI verbinden
          </Link>
          <Link href="/admin/anbindungen" className={path === '/admin/anbindungen' ? 'is-active' : ''}>
            Anbindungen
          </Link>
          <Link href="/admin/konto" className={path === '/admin/konto' ? 'is-active' : ''}>
            Konto
          </Link>
        </nav>
        <span className="admin-bar__spacer" />
        <span className="muted small">{identity.user.email}</span>
        <button type="button" className="button button--secondary button--small" onClick={logout}>
          Abmelden
        </button>
      </header>
      <main className="admin-main">{page}</main>
    </>
  );
}

function LoginPage({ meta, onLogin, onForgot }: { meta: Meta; onLogin: (identity: Identity) => void; onForgot: () => void }) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onLogin(mode === 'login' ? await adminApi.login(email, password) : await adminApi.register(email, password, name));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setError('E-Mail-Adresse oder Passwort stimmen nicht.');
      else if (e instanceof ApiError && e.status === 429) setError('Zu viele Versuche. Bitte in einer Stunde noch einmal.');
      else setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page" style={{ maxWidth: 440, paddingTop: 96 }}>
      <div>
        <p className="label label--brand">{meta.productName}</p>
        <h1 style={{ fontSize: 28, marginTop: 8 }}>{mode === 'login' ? 'Anmelden' : 'Zugang anlegen'}</h1>
      </div>
      <form className="card" onSubmit={submit}>
        {mode === 'register' && (
          <div className="field">
            <label htmlFor="name">Dein Name</label>
            <input id="name" type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
        )}
        <div className="field">
          <label htmlFor="email">E-Mail-Adresse</label>
          <input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="password">Passwort</label>
          <input id="password" type="password" autoComplete={mode === 'login' ? 'current-password' : 'new-password'} required value={password} onChange={(e) => setPassword(e.target.value)} />
          {mode === 'register' && <span className="muted small">Mindestens 10 Zeichen.</span>}
        </div>
        {error && <div className="error">{error}</div>}
        <button type="submit" className="button" disabled={busy}>
          {busy ? 'Einen Moment …' : mode === 'login' ? 'Anmelden' : 'Zugang anlegen'}
        </button>
        {mode === 'login' && (
          <button type="button" className="link-button" onClick={onForgot}>
            Passwort vergessen?
          </button>
        )}
        {meta.registrationOpen && (
          <button type="button" className="link-button" onClick={() => setMode(mode === 'login' ? 'register' : 'login')}>
            {mode === 'login' ? 'Noch keinen Zugang? Hier anlegen.' : 'Schon einen Zugang? Hier anmelden.'}
          </button>
        )}
      </form>
    </main>
  );
}
